/**
 * Memberships API client — typed fetch functions for every endpoint (Phase 10D).
 *
 * Membership types are defined here because the generated shared/api-types
 * index does not yet include the Phase 10D memberships endpoints.
 */

import { api } from '@core/api/client';

// ---------------------------------------------------------------------------
// Domain types — mirror backend Pydantic schemas exactly
// ---------------------------------------------------------------------------

export type MembershipStatus = 'active' | 'paused' | 'cancelled';

export type RecurrenceUnit = 'day' | 'week' | 'month' | 'year';

export interface RecurrencePattern {
  unit: RecurrenceUnit;
  interval: number;
  day_of_month?: number | null;
  month_of_year?: number | null;
}

export interface Membership {
  id: string;
  tenant_id: string;
  student_id: string;
  student_name_snapshot: string;
  package_id: string;
  package_name_snapshot: string;
  amount: number; // paise
  currency: string;
  status: MembershipStatus;
  recurrence: RecurrencePattern;
  first_invoice_date: string; // ISO date
  next_invoice_date: string; // ISO date
  last_invoice_date: string | null;
  last_invoice_id: string | null;
  created_at: string;
  updated_at: string;
  paused_at: string | null;
  paused_until: string | null;
  pause_reason: string | null;
  cancelled_at: string | null;
  cancel_reason: string | null;
  cancelled_by_user_id: string | null;
  skip_renewal_dates: string[];
  created_by_user_id: string;
  classcard_id: string | null;
}

export interface MembershipRenewal {
  id: string;
  membership_id: string;
  renewal_date: string; // ISO date
  status: 'generated' | 'skipped' | 'failed' | 'manual';
  invoice_id: string | null;
  invoice_number: string | null;
  amount: number;
  skip_reason: string | null;
  failure_reason: string | null;
  failure_code: string | null;
  generated_at: string;
  generated_by: 'cron' | 'admin_manual' | 'migration';
  retry_count: number;
}

export interface MembershipDLQEntry {
  id: string;
  membership_id: string;
  membership_snapshot: Record<string, unknown>;
  renewal_date: string;
  failure_code: string;
  failure_message: string;
  stack_trace: string | null;
  occurred_at: string;
  cron_run_id: string;
  retry_count: number;
  resolved: boolean;
  resolved_at: string | null;
  resolution_notes: string | null;
  burned_invoice_numbers: string[];
}

export interface MembershipListResponse {
  items: Membership[];
  next_cursor: string | null;
}

export interface MembershipRenewalListResponse {
  items: MembershipRenewal[];
}

export interface MembershipDLQListResponse {
  items: MembershipDLQEntry[];
}

// ---------------------------------------------------------------------------
// Request types
// ---------------------------------------------------------------------------

export interface MembershipCreateRequest {
  student_id: string;
  package_id: string;
  amount: number;
  currency?: string;
  recurrence: RecurrencePattern;
  first_invoice_date: string; // ISO date YYYY-MM-DD
  classcard_id?: string | null;
}

export interface MembershipUpdateRequest {
  amount?: number;
  recurrence?: RecurrencePattern;
  classcard_id?: string | null;
}

export interface MembershipPauseRequest {
  paused_until: string; // ISO date
  reason: string;
}

export interface MembershipCancelRequest {
  reason: string;
}

export type DLQResolution = 'retry' | 'skip' | 'pause' | 'mark_resolved';

export interface MembershipDLQResolveRequest {
  resolution: DLQResolution;
  notes: string;
}

export type BillingCycle = 'monthly' | 'quarterly' | 'half_yearly' | 'yearly';

export interface ListMembershipsParams {
  status?: MembershipStatus | '';
  student_id?: string;
  package_id?: string;
  /** 11E.8/D2 — joins via the catalog Package (billing_cycle isn't on the
   * Membership doc itself). */
  billing_cycle?: BillingCycle | '';
  /** 11E.8/D2 — YYYY-MM, filters on next_invoice_date falling in the month. */
  renewal_month?: string;
  limit?: number;
}

// ---------------------------------------------------------------------------
// API functions
// ---------------------------------------------------------------------------

export async function listMemberships(
  params: ListMembershipsParams = {},
): Promise<MembershipListResponse> {
  return api.get<MembershipListResponse>('/v1/memberships', {
    query: {
      status: params.status || undefined,
      student_id: params.student_id || undefined,
      package_id: params.package_id || undefined,
      billing_cycle: params.billing_cycle || undefined,
      renewal_month: params.renewal_month || undefined,
      limit: params.limit,
    },
  });
}

export async function getMembership(id: string): Promise<Membership> {
  return api.get<Membership>(`/v1/memberships/${id}`);
}

export async function createMembership(
  body: MembershipCreateRequest,
): Promise<Membership> {
  return api.post<Membership>('/v1/memberships', body);
}

export async function updateMembership(
  id: string,
  body: MembershipUpdateRequest,
): Promise<Membership> {
  return api.patch<Membership>(`/v1/memberships/${id}`, body);
}

export async function pauseMembership(
  id: string,
  body: MembershipPauseRequest,
): Promise<Membership> {
  return api.post<Membership>(`/v1/memberships/${id}/pause`, body);
}

export async function resumeMembership(id: string): Promise<Membership> {
  return api.post<Membership>(`/v1/memberships/${id}/resume`);
}

export async function skipNextMembership(id: string): Promise<Membership> {
  return api.post<Membership>(`/v1/memberships/${id}/skip-next`);
}

export async function cancelMembership(
  id: string,
  body: MembershipCancelRequest,
): Promise<Membership> {
  return api.post<Membership>(`/v1/memberships/${id}/cancel`, body);
}

export async function listMembershipRenewals(
  id: string,
  limit = 50,
): Promise<MembershipRenewalListResponse> {
  return api.get<MembershipRenewalListResponse>(
    `/v1/memberships/${id}/renewals`,
    { query: { limit } },
  );
}

export async function listDLQEntries(
  resolved?: boolean,
): Promise<MembershipDLQListResponse> {
  return api.get<MembershipDLQListResponse>('/v1/memberships/dlq', {
    query: { resolved: resolved !== undefined ? resolved : undefined },
  });
}

export async function resolveDLQEntry(
  dlqId: string,
  body: MembershipDLQResolveRequest,
): Promise<MembershipDLQEntry> {
  return api.post<MembershipDLQEntry>(`/v1/memberships/dlq/${dlqId}/resolve`, body);
}
